import { useT } from "@transifex/react";
import { useEffect, useMemo, useState } from "react";
import { ArrowsMergeIcon, ListChecksIcon } from "@phosphor-icons/react";
import Checkbox from "../../../basic/checkbox/Checkbox";
import IconButton from "../../../basic/iconButton/IconButton";
import LoadingSpinner from "../../../basic/loadingSpinner/LoadingSpinner";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import { toggleInSet } from "../../../helpers/SetHelper";
import type { ClassMember } from "../../../networking/schoolClasses";
import useBreakpoints from "../../../hooks/useBreakpoints";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../../teacherArea/AreaButton";
import { FormattedText } from "../../teacherArea/FormattedText";
import ClassMemberRow from "./ClassMemberRow";

interface ClassMembersSectionProps {
    classMembers: ClassMember[];
    /** Keeps the card in place with a spinner instead of flashing "0" + a primary add action. */
    isLoading: boolean;
    hasLoadError: boolean;
    newMemberIds: Set<string>;
    duplicateGroups: ClassMember[][];
    onAddClassMembers: () => void;
    onShareMemberAccess: (classMember: ClassMember) => void;
    onEditMember: (classMember: ClassMember) => void;
    onDeleteMember: (classMember: ClassMember) => void;
    onReviewDuplicates: (group: ClassMember[]) => void;
    onMergeSelection: (selected: ClassMember[]) => void;
    onDeleteSelection: (selected: ClassMember[]) => void;
}

interface MemberRow {
    member: ClassMember;
    group: ClassMember[] | null;
}

interface RowSegment {
    group: ClassMember[] | null;
    rows: MemberRow[];
    startNumber: number;
}

const INITIAL_ITEMS_SHOWN = 3;

const ClassMembersSection = (props: ClassMembersSectionProps) => {
    const t = useT();
    const { isAboveMD: isDesktop } = useBreakpoints();

    const [showAll, setShowAll] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    const t_members = t("Students", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_add = t("Add", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_select = t("Select", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_done = t("Done", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_selectAll = t("Select all", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_selectedCount = (count: number) =>
        t("{count} selected", { _tags: "SchoolClasses, ClassMembersSection", count });
    const t_mergeTogether = t("Merge together", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_merge = t("Merge", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_delete = t("Delete", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_sr_endSelection = t("End selection", {
        _tags: "SchoolClasses, ClassMembersSection, ScreenReaderOnly",
    });
    const t_loadError = t("Could not load students. Please try again later.", {
        _tags: "SchoolClasses, ClassMembersSection",
    });
    const t_showMore = t("show more", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_showLess = t("show less", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_new = t("new", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_possibleDuplicate = t("possible duplicate", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_possibleDuplicates = t("possible duplicates", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_maybeDoubled = t("maybe doubled", { _tags: "SchoolClasses, ClassMembersSection" });
    const t_samePerson = (first: string, last: string) =>
        t("Are <b>{first}</b> to <b>{last}</b> the same person?", {
            _tags: "SchoolClasses, ClassMembersSection",
            first,
            last,
        });
    const t_review = t("Review", { _tags: "SchoolClasses, ClassMembersSection" });

    const hasClassMembers = props.classMembers.length > 0;
    const hasMoreClassMembers = props.classMembers.length > INITIAL_ITEMS_SHOWN;

    const newCount = props.classMembers.filter((member) => props.newMemberIds.has(member.id)).length;
    const groupCount = props.duplicateGroups.length;

    // Stale ids are harmless: a member merged or deleted meanwhile drops out here.
    const selectedMembers = props.classMembers.filter((member) => selectedIds.has(member.id));
    const areAllSelected = hasClassMembers && selectedMembers.length === props.classMembers.length;

    const rowsWithGroupsTogether = useMemo((): MemberRow[] => {
        const groupByMemberId = new Map<string, ClassMember[]>();
        props.duplicateGroups.forEach((group) => group.forEach((member) => groupByMemberId.set(member.id, group)));
        const placed = new Set<string>();
        const rows: MemberRow[] = [];
        props.classMembers.forEach((member) => {
            if (placed.has(member.id)) return;
            const group = groupByMemberId.get(member.id) ?? null;
            (group ?? [member]).forEach((groupMember) => {
                placed.add(groupMember.id);
                rows.push({ member: groupMember, group });
            });
        });
        return rows;
    }, [props.classMembers, props.duplicateGroups]);

    // While selecting, the whole list shows so "Select all" never reaches rows behind the fold; on phones it
    // scrolls inside the card instead of growing, so the selection footer stays in reach.
    const isScrollingList = isSelecting && !isDesktop;
    const visibleRows =
        showAll || isSelecting ? rowsWithGroupsTogether : rowsWithGroupsTogether.slice(0, INITIAL_ITEMS_SHOWN);

    // From the full list, so a group cut by the "show more" fold still announces its whole range.
    const lastNumberByGroup = new Map<ClassMember[], number>();
    rowsWithGroupsTogether.forEach((row, index) => {
        if (row.group) lastNumberByGroup.set(row.group, index + 1);
    });

    const segments = visibleRows.reduce((acc: RowSegment[], row, index) => {
        const last = acc[acc.length - 1];
        if (last && row.group && last.group === row.group) {
            last.rows.push(row);
        } else {
            acc.push({ group: row.group, rows: [row], startNumber: index + 1 });
        }
        return acc;
    }, []);

    // Wide enough for the highest row number so the names stay aligned in one column.
    const numberWidthClass = props.classMembers.length > 9 ? "min-w-[24px]" : "min-w-[12px]";
    const numberClass = `${numberWidthClass} text-right shrink-0`;

    const startSelecting = () => {
        setSelectedIds(new Set());
        setIsSelecting(true);
    };

    const endSelecting = () => {
        setSelectedIds(new Set());
        setIsSelecting(false);
    };

    const toggleAll = () =>
        setSelectedIds(areAllSelected ? new Set() : new Set(props.classMembers.map((member) => member.id)));

    // No members left (e.g. all deleted): leave selection mode, or the header keeps hiding its buttons.
    useEffect(() => {
        if (isSelecting && !hasClassMembers) endSelecting();
    }, [isSelecting, hasClassMembers]);

    const renderAddButton = () => (
        <AreaButton
            data-testid="button-add-class-members"
            // Secondary even for an empty class: sharing access is the primary next step there.
            variant={ButtonVariant.SECONDARY}
            colorVariant={ButtonColorVariant.DEFAULT_WHITE}
            role="button"
            onClick={props.onAddClassMembers}
            iconName="add"
            iconPosition={IconPosition.RIGHT}
            className={isDesktop ? undefined : "w-full"}
        >
            {t_add}
        </AreaButton>
    );

    const renderSelectButton = () => (
        <button
            type="button"
            data-testid="button-select-class-members"
            onClick={startSelecting}
            className={`flex items-center justify-center gap-xs min-h-xl text-l font-semibold text-p6-grey-dark hover:text-p6-grey-darkest bg-transparent border-none p-0 cursor-pointer ${
                isDesktop ? "" : "w-full"
            }`}
        >
            {t_select}
            <PhosphorIcon
                phosphorIcon={ListChecksIcon}
                fontSize="text-xl"
            />
        </button>
    );

    const renderDoneButton = () => (
        <AreaButton
            data-testid="button-end-selection"
            variant={ButtonVariant.SECONDARY}
            colorVariant={ButtonColorVariant.DEFAULT_WHITE}
            role="button"
            onClick={endSelecting}
            className="min-w-[160px]"
        >
            {t_done}
        </AreaButton>
    );

    const renderHeader = () => {
        if (props.isLoading) {
            return (
                <div className="flex items-center justify-center p-m w-full">
                    <LoadingSpinner size="h-xl w-xl" />
                </div>
            );
        }

        const title = (
            <div className="flex items-center gap-s flex-1 min-w-0">
                <PhaseSixIcon
                    name="group"
                    className="text-card-icon text-p6-grey-dark shrink-0"
                />
                <span className="text-xl font-semibold text-p6-grey-dark min-w-0">
                    <span data-testid="class-members-count">{props.classMembers.length}</span> {t_members}
                </span>
            </div>
        );

        if (isSelecting) {
            return (
                <div className="flex items-center justify-between gap-m p-m w-full">
                    {title}
                    {isDesktop && renderDoneButton()}
                </div>
            );
        }

        return (
            <div className={`flex gap-m p-m w-full ${isDesktop ? "flex-row items-center" : "flex-col"}`}>
                {title}
                {isDesktop && hasClassMembers && renderSelectButton()}
                {renderAddButton()}
                {!isDesktop && hasClassMembers && renderSelectButton()}
            </div>
        );
    };

    const renderSummaryBar = () => {
        if (newCount + groupCount === 0) return null;

        let duplicatesLabel = t_maybeDoubled;
        if (isDesktop) {
            duplicatesLabel = groupCount === 1 ? t_possibleDuplicate : t_possibleDuplicates;
        }

        return (
            <div
                data-testid="class-members-summary"
                className="flex flex-wrap items-center gap-s px-m py-xxs bg-p6-orange-light-pastel text-m text-p6-orange-darker border-y border-p6-orange-pastel"
            >
                {newCount > 0 && (
                    <span>
                        <span className="text-l">{newCount}</span> {t_new}
                    </span>
                )}
                {newCount > 0 && groupCount > 0 && <span>•</span>}
                {groupCount > 0 && (
                    <span>
                        <span className="text-l">{groupCount}</span> {duplicatesLabel}
                    </span>
                )}
            </div>
        );
    };

    const getSelectAllAriaChecked = (): boolean | "mixed" => {
        if (areAllSelected) return true;
        if (selectedMembers.length > 0) return "mixed";
        return false;
    };

    const renderSelectAllButton = () => (
        <button
            type="button"
            role="checkbox"
            aria-checked={getSelectAllAriaChecked()}
            data-testid="class-members-select-all"
            onClick={toggleAll}
            className={`flex items-center gap-s min-h-xl text-l text-p6-grey-dark bg-transparent border-none p-0 cursor-pointer ${
                isDesktop ? "font-semibold" : ""
            }`}
        >
            <Checkbox
                isChecked={areAllSelected}
                isPartial={selectedMembers.length > 0}
            />
            <span data-testid="class-members-selected-count">
                {selectedMembers.length === 0 ? t_selectAll : t_selectedCount(selectedMembers.length)}
            </span>
        </button>
    );

    const canMerge = selectedMembers.length >= 2;
    const canDelete = selectedMembers.length >= 1;

    const renderSelectionAction = (
        testId: string,
        label: string,
        icon: JSX.Element,
        isEnabled: boolean,
        onClick: () => void
    ) => (
        <button
            type="button"
            data-testid={testId}
            disabled={!isEnabled}
            onClick={onClick}
            className="flex items-center text-m text-p6-grey-dark bg-transparent border-none p-0 enabled:cursor-pointer enabled:hover:text-p6-grey-darkest disabled:opacity-30 disabled:cursor-not-allowed"
        >
            {label}
            <span className="flex items-center justify-center min-w-[44px] min-h-[44px]">{icon}</span>
        </button>
    );

    const renderSelectionBar = () => (
        <div
            data-testid="class-members-selection-bar"
            className={`flex items-center justify-between gap-s px-m pb-xs border-b-2 border-p6-grey-light ${
                isDesktop ? "" : "pt-xxs"
            }`}
        >
            {renderSelectAllButton()}
            {isDesktop ? (
                <div className="flex items-center gap-m">
                    {renderSelectionAction(
                        "button-merge-selection",
                        t_mergeTogether,
                        <PhosphorIcon
                            phosphorIcon={ArrowsMergeIcon}
                            fontSize="text-header-icon"
                        />,
                        canMerge,
                        () => props.onMergeSelection(selectedMembers)
                    )}
                    <div
                        className="self-stretch border-l border-p6-grey-mid"
                        aria-hidden="true"
                    />
                    {renderSelectionAction(
                        "button-delete-selection",
                        t_delete,
                        <PhaseSixIcon
                            name="trash"
                            className="text-header-icon"
                        />,
                        canDelete,
                        () => props.onDeleteSelection(selectedMembers)
                    )}
                </div>
            ) : (
                <IconButton
                    data-testid="button-end-selection"
                    iconName="close"
                    ariaLabel={t_sr_endSelection}
                    onClick={endSelecting}
                    size="text-header-icon"
                    iconAlignment="right"
                />
            )}
        </div>
    );

    const renderSelectionFooter = () => (
        <div className="flex gap-s px-s pt-m pb-s border-t-2 border-p6-grey-light">
            <AreaButton
                data-testid="button-merge-selection"
                variant={ButtonVariant.SECONDARY}
                colorVariant={ButtonColorVariant.DEFAULT_WHITE}
                role="button"
                onClick={() => props.onMergeSelection(selectedMembers)}
                disabled={!canMerge}
                phosphorIcon={ArrowsMergeIcon}
                iconPosition={IconPosition.RIGHT}
                className="flex-1"
            >
                {t_merge}
            </AreaButton>
            <AreaButton
                data-testid="button-delete-selection"
                variant={ButtonVariant.SECONDARY}
                colorVariant={ButtonColorVariant.DANGER}
                role="button"
                onClick={() => props.onDeleteSelection(selectedMembers)}
                disabled={!canDelete}
                iconName="trash"
                iconPosition={IconPosition.RIGHT}
                hideLabel
            >
                {t_delete}
            </AreaButton>
        </div>
    );

    const renderRow = (row: MemberRow, number: number) => (
        <ClassMemberRow
            key={row.member.id}
            classMember={row.member}
            index={number - 1}
            numberClass={numberClass}
            isNew={props.newMemberIds.has(row.member.id)}
            isInDuplicateGroup={row.group !== null}
            selection={
                isSelecting
                    ? {
                          isSelected: selectedIds.has(row.member.id),
                          onToggle: () => setSelectedIds((prev) => toggleInSet(prev, row.member.id)),
                      }
                    : undefined
            }
            onShareAccess={props.onShareMemberAccess}
            onEdit={props.onEditMember}
            onDelete={props.onDeleteMember}
        />
    );

    const renderGroupHeader = (group: ClassMember[], firstNumber: number, lastNumber: number) => (
        <div
            data-testid="duplicate-group-header"
            className={`flex gap-s px-m py-xs bg-p6-buttercup-100 border-y border-p6-orange-pastel ${
                isDesktop ? "flex-row items-center justify-between" : "flex-col-reverse"
            }`}
        >
            <FormattedText
                rawText={t_samePerson(String(firstNumber), String(lastNumber))}
                textColor="text-m text-p6-buttercup-1000 [&>b]:font-semibold"
            />
            <button
                type="button"
                data-testid="button-review-duplicates"
                onClick={() => props.onReviewDuplicates(group)}
                className="flex items-center gap-xs self-end text-l font-semibold text-p6-buttercup-1000 bg-transparent border-none p-0 cursor-pointer"
            >
                {t_review}
                <PhaseSixIcon
                    name="chevron-right"
                    className="text-xl"
                />
            </button>
        </div>
    );

    const renderSegment = (segment: RowSegment) => {
        const rows = segment.rows.map((row, offset) => renderRow(row, segment.startNumber + offset));
        if (!segment.group || isSelecting) return rows;

        const lastNumber = lastNumberByGroup.get(segment.group) ?? segment.startNumber;
        return (
            <div
                key={`group-${segment.group[0].id}`}
                data-testid="duplicate-group"
                className="flex flex-col border-x-4 border-p6-buttercup-800"
            >
                {renderGroupHeader(segment.group, segment.startNumber, lastNumber)}
                {rows}
            </div>
        );
    };

    const renderShowMoreToggle = () => {
        if (!hasMoreClassMembers || isSelecting) return null;

        return (
            <div className="flex justify-center border-t border-p6-grey-lightest w-full">
                <button
                    type="button"
                    aria-expanded={showAll}
                    data-testid="class-members-show-more"
                    onClick={() => setShowAll((prev) => !prev)}
                    className="flex items-center justify-center gap-xs text-l text-p6-grey-dark cursor-pointer bg-transparent border-none py-s"
                >
                    <span>{showAll ? t_showLess : t_showMore}</span>
                    <PhaseSixIcon
                        name="chevron-down"
                        className={`text-header-icon transition-transform ${showAll ? "rotate-180" : ""}`}
                    />
                </button>
            </div>
        );
    };

    return (
        <div
            className="flex flex-col bg-white border border-p6-grey-lightest rounded-medium overflow-clip w-full"
            data-testid="class-members-section"
        >
            {renderHeader()}
            {hasClassMembers && (
                <>
                    {isSelecting ? renderSelectionBar() : renderSummaryBar()}
                    <div
                        data-testid="class-members-list"
                        className={`flex flex-col w-full px-s pb-xs ${
                            isScrollingList ? "max-h-[var(--size-class-member-selection-list)] overflow-y-auto" : ""
                        }`}
                    >
                        {segments.map(renderSegment)}
                        {renderShowMoreToggle()}
                    </div>
                    {isSelecting && !isDesktop && renderSelectionFooter()}
                </>
            )}
            {!hasClassMembers && props.hasLoadError && (
                <div className="flex items-center justify-center border-t border-p6-grey-lightest px-m py-m">
                    <span className="text-l text-p6-grey-mid">{t_loadError}</span>
                </div>
            )}
        </div>
    );
};

export default ClassMembersSection;
